import { EDITOR_TEXT_BOX_ITEM_TYPE } from '@messenger/types'
import { resolveEditorTextBoxDocument } from '@messenger/lotus'
import type { EditorTextBoxDocument } from '@messenger/lotus'
import type { EditorPageContentBundle } from './editorTextBoxBundle'

export type EditorTextBoxItemLike = {
  id?: unknown
  type?: unknown
  x?: unknown
  y?: unknown
  z?: unknown
  width?: unknown
  height?: unknown
  assetLocked?: unknown
  hideInPdf?: unknown
  edits?: { rotate?: unknown } | null
}

/** A page item's stored number, a numeric string included; null for anything else. */
export const asNumber = (value: unknown): number | null => {
  const parsed =
    typeof value === 'string' && value.trim() !== '' ? Number(value) : value
  return typeof parsed === 'number' && Number.isFinite(parsed) ? parsed : null
}

/** A number at the four decimal places a box row stores. */
export const asStored = (value: number): number =>
  Math.round(value * 10000) / 10000

export const editorAnchorPageId = (
  anchor: EditorTextBoxDocument['anchor'],
): string =>
  anchor.orderPageId ??
  anchor.m3OrderPageId ??
  anchor.globalPageId ??
  anchor.funeralHomePageId ??
  ''

export const editorTextBoxWithItemGeometry = (
  box: EditorTextBoxDocument['box'],
  item: EditorTextBoxItemLike,
): EditorTextBoxDocument['box'] => {
  const columns: Partial<EditorTextBoxDocument['box']> = {}

  const x = asNumber(item.x)
  if (x !== null && asStored(x) !== asStored(box.x)) columns.x = asStored(x)
  const y = asNumber(item.y)
  if (y !== null && asStored(y) !== asStored(box.y)) columns.y = asStored(y)
  const width = asNumber(item.width)
  if (width !== null) {
    const quantized = asStored(width)
    if (quantized > 0 && quantized !== asStored(box.width))
      columns.width = quantized
  }
  const height = asNumber(item.height)
  if (height !== null) {
    const quantized = asStored(height)
    if (quantized >= 0 && quantized !== asStored(box.height))
      columns.height = quantized
  }
  const z = asNumber(item.z)
  if (z !== null) {
    const zIndex = Math.round(z)
    if (zIndex !== (box.zIndex ?? 0)) columns.zIndex = zIndex
  }
  const rotation = asNumber(item.edits?.rotate) ?? 0
  if (asStored(rotation) !== asStored(box.rotation ?? 0))
    columns.rotation = asStored(rotation)
  if (
    typeof item.assetLocked === 'boolean' &&
    item.assetLocked !== (box.isLocked ?? false)
  )
    columns.isLocked = item.assetLocked
  if (
    typeof item.hideInPdf === 'boolean' &&
    item.hideInPdf !== (box.isHiddenInPdf ?? false)
  )
    columns.isHiddenInPdf = item.hideInPdf

  return Object.keys(columns).length === 0 ? box : { ...box, ...columns }
}

type ViewModelResultLike = {
  document: EditorTextBoxDocument | null
}

export const editorTextBoxGeometryDraftUpdates = (args: {
  items: readonly EditorTextBoxItemLike[]
  results: ReadonlyMap<string, ViewModelResultLike> | null
  drafts: Readonly<Record<string, EditorTextBoxDocument>>
  pendingDeleteIds: ReadonlySet<string>
}): Record<string, EditorTextBoxDocument> => {
  const updates: Record<string, EditorTextBoxDocument> = {}
  for (const item of args.items) {
    const id = typeof item.id === 'string' ? item.id : null
    if (
      id === null ||
      item.type !== EDITOR_TEXT_BOX_ITEM_TYPE ||
      args.pendingDeleteIds.has(id)
    )
      continue
    const base = args.drafts[id] ?? args.results?.get(id)?.document ?? null
    if (base === null) continue
    const nextBox = editorTextBoxWithItemGeometry(base.box, item)
    if (nextBox === base.box) continue
    try {
      updates[id] = resolveEditorTextBoxDocument({ ...base, box: nextBox })
    } catch (error) {
      console.error(
        `editor text box ${id}: canvas geometry could not be folded into the draft — the box keeps its previous state`,
        error,
      )
    }
  }
  return updates
}

/**
 * A box's document as the canvas shows it now: its draft, or else its saved
 * row, with the canvas item's geometry folded in the way a save folds it. A
 * drag, a resize or Hide in PDF changes only the canvas item until a save, so
 * the clipboard copies this rather than the draft alone.
 */
export const editorTextBoxCurrentDocument = (args: {
  item: EditorTextBoxItemLike
  results: ReadonlyMap<string, ViewModelResultLike> | null
  drafts: Readonly<Record<string, EditorTextBoxDocument>>
}): EditorTextBoxDocument | null => {
  const id = typeof args.item.id === 'string' ? args.item.id : null
  if (id === null) return null
  const folded = editorTextBoxGeometryDraftUpdates({
    items: [args.item],
    results: args.results,
    drafts: args.drafts,
    pendingDeleteIds: new Set(),
  })
  return (
    folded[id] ?? args.drafts[id] ?? args.results?.get(id)?.document ?? null
  )
}

const bundleRevisions = (
  bundle: EditorPageContentBundle,
): Map<string, number> =>
  new Map(bundle.boxes.map((box) => [box.boxId, box.revision]))

export const editorTextBoxDraftsAfterConflict = (
  drafts: Readonly<Record<string, EditorTextBoxDocument>>,
  pageId: string,
  bundle: EditorPageContentBundle,
): Record<string, EditorTextBoxDocument> => {
  const revisions = bundleRevisions(bundle)
  const kept: Record<string, EditorTextBoxDocument> = {}
  for (const [boxId, draft] of Object.entries(drafts)) {
    if (editorAnchorPageId(draft.anchor) !== pageId) {
      kept[boxId] = draft
      continue
    }
    if (revisions.get(boxId) === draft.revision) kept[boxId] = draft
  }
  return kept
}

export const editorTextBoxPendingDeletesAfterConflict = (
  pendingDeletes: Readonly<Record<string, string>>,
  pageId: string,
  bundle: EditorPageContentBundle,
): Record<string, string> => {
  const revisions = bundleRevisions(bundle)
  const kept: Record<string, string> = {}
  for (const [boxId, deletePageId] of Object.entries(pendingDeletes)) {
    if (deletePageId !== pageId) {
      kept[boxId] = deletePageId
      continue
    }
    if (revisions.has(boxId)) kept[boxId] = deletePageId
  }
  return kept
}
